<template>
	<Dialog
		class="column-form"
		modal
		:header="!props.editing ? 'Create column' : 'Edit column'"
		:visible="!!form"
		@update:visible="emit('cancel')">
		<form
			v-if="form"
			class="column-form__form"
			@submit.prevent="handleSave()">
			<div class="column-form__form-section-container">
				<div class="column-form__basic-info-container">
					<TypeSelect :column="form" />
					<NameInput
						:column="form"
						:module="moduleForm" />
				</div>
				<EnumValues :column="form" />
				<FakerMethodSelect :column="form" />
				<ValidatorsSelect :column="form" />
				<AdditionalProperties
					:column="form"
					:module="moduleForm" />
			</div>
			<Divider />
			<div class="column-form__footer">
				<Button
					icon="fal xmark"
					label="Cancel"
					severity="secondary"
					@click="handleCancel()" />
				<Button
					icon="fal fa-floppy-disk"
					label="Save"
					severity="primary"
					type="submit"
					@submit.prevent="handleSave()" />
			</div>
		</form>
	</Dialog>
</template>

<script setup lang="ts">
import Divider from 'primevue/divider'
import Dialog from 'primevue/dialog'
import { ref, watch, type ComponentPublicInstance, type Ref } from 'vue'
import { EnrichedColumn } from '@codecannon/schemas/schemas/EnrichedColumn'
import { EnrichedModule } from '@codecannon/schemas/schemas/EnrichedModule'
import { Button } from 'primevue'
import InputText from 'primevue/inputtext'
import TypeSelect from './ColumnForm/TypeSelect.vue'
import NameInput from './ColumnForm/NameInput.vue'
import EnumValues from './ColumnForm/EnumValues.vue'
import FakerMethodSelect from './ColumnForm/FakerMethodSelect.vue'
import ValidatorsSelect from './ColumnForm/ValidatorsSelect.vue'
import AdditionalProperties from './ColumnForm/AdditionalProperties.vue'

const emit = defineEmits<{
	(
		e: 'save',
		updatePayload: {
			title: string
			subtitle: string | null
			column: EnrichedColumn
		},
	): void
	(e: 'cancel'): void
}>()

const props = defineProps<{
	module: EnrichedModule
	column: EnrichedColumn | null
	editing: boolean
}>()

const { form, moduleForm } = useForm()

function handleCancel() {
	emit('cancel')
}

function handleSave() {
	if (!form.value) return
	emit('save', {
		title:
			Object.keys(props.module.model.columns).length === 0
				? form.value.name
				: moduleForm.value.model.title,
		subtitle: moduleForm.value.model.subtitle,
		column: form.value,
	})
}

function useForm() {
	const moduleForm = ref<EnrichedModule>(
		new EnrichedModule(props.module.toPOJO()),
	) as Ref<EnrichedModule>
	const form = ref<EnrichedColumn | null>(
		props.column ? new EnrichedColumn(props.column.toPOJO(), moduleForm.value.model) : null,
	) as Ref<EnrichedColumn | null>
	const nameInput = ref<ComponentPublicInstance<typeof InputText> | null>(null)

	watch(
		() => props.column,
		() => {
			moduleForm.value = new EnrichedModule(props.module.toPOJO())
			form.value = props.column
				? new EnrichedColumn(props.column.toPOJO(), moduleForm.value.model)
				: null
		},
	)

	return {
		form,
		moduleForm,
		nameInput,
	}
}
</script>

<style lang="scss" scoped>
.column-form {
	form {
		display: flex;
		flex-direction: column;
		gap: 10px;
		width: 600px;

		.column-form__form-section-container {
			padding: 5px;

			.column-form__basic-info-container {
				display: flex;
				gap: 16px;
				align-items: center;
				margin-bottom: 16px;
			}
		}
	}

	.column-form__footer {
		display: flex;
		justify-content: flex-end;
		gap: 8px;
	}
}
</style>
